import { Directive, ElementRef, Input, OnInit, booleanAttribute, output } from '@angular/core';
import { MANGLISH_LOOKUP } from '@otas/utils/directives/extras/manglish-layout';

export type ManglishMode = 'english' | 'manglish';

/**
 * Precomputed frozen, prototype-less index over the shared legacy layout
 * table (same keys/values as MANGLISH_LAYOUT, spec-pinned there).
 */
const LAYOUT: Readonly<Record<string, string>> = MANGLISH_LOOKUP;

/** Legacy parity: patterns longer than this restart with the current key. */
const MAX_PATTERN_LENGTH = 5;

/** Splits a pattern into its base and trailing alternate digits ('ka2' -> ['ka', '2']). */
function splitPattern(pattern: string): [string, string] {
    const match = /^(.*?)(\d*)$/.exec(pattern);
    return match ? [match[1], match[2]] : [pattern, ''];
}

function initialMode(): ManglishMode {
    try {
        const raw = sessionStorage.getItem('CURRENT_USER');
        if (!raw) {
            return 'english';
        }
        const user: { typingLanguage?: string } = JSON.parse(raw);
        const language = (user?.typingLanguage || '').toLowerCase();
        return language === 'manglish' || language === 'malayalam' ? 'manglish' : 'english';
    } catch {
        return 'english';
    }
}

/**
 * Opt-in Manglish (Malayalam transliteration) layer for plain text inputs —
 * the standalone replacement for the legacy `ManglishConverterDirective`
 * (`src/utils/directives/manglish/`), sharing its `MANGLISH_LAYOUT` table and
 * keystroke behavior but NOT its custom `minput` output: every programmatic
 * value mutation here dispatches a native bubbling `input` event, so `(input)`
 * bindings, ControlValueAccessors and reactive forms all see the change.
 *
 * Enable with `[appManglish]="true"` (or the bare `appManglish` attribute);
 * when the input is false/absent the directive is completely inert.
 *
 * Behavior (parity with the legacy keydown algorithm):
 * - Printable keys accumulate an ASCII `pattern`; on a `MANGLISH_LAYOUT` hit
 *   the Malayalam glyph is spliced in at the pattern start (replacing the
 *   previous partial glyph); on a miss the pattern restarts with the key and
 *   the key is inserted literally. Patterns longer than 5 chars restart.
 * - Tab cycles alternates (base -> base2 -> base3 ... -> wrap to base) while
 *   a glyph-producing pattern is active; otherwise Tab is left to the browser
 *   (focus navigation — deliberate fix over legacy, which swallowed it).
 * - Backspace/Delete/Space reset the pattern; the browser performs the edit.
 * - F6 toggles english <-> manglish (`modeChange`); in english mode typing is
 *   fully native. Initial mode comes from sessionStorage
 *   CURRENT_USER.typingLanguage ('manglish'/'malayalam' -> manglish).
 * - `patternChange` mirrors the in-progress pattern (null when reset) so
 *   hosts can render an alternates strip; `selectAlternate` lets a host swap
 *   the last committed glyph for a clicked alternate (what Tab would do).
 */
@Directive({
    selector: 'input[appManglish]',
    host: {
        '(keydown)': 'onKeydown($event)',
        '(blur)': 'onBlur()'
    }
})
export class AppManglishDirective implements OnInit {
    /** Master switch — when false the directive does nothing at all. */
    @Input({ transform: booleanAttribute }) appManglish = false;

    /**
     * Starting mode for hosts that already know the typing language, overriding
     * the sessionStorage default. Needed because `initialMode()` reads
     * CURRENT_USER, which a PRE-LOGIN screen (temple registration) does not
     * have — there the mode would always resolve to english and the മ badge
     * would never appear until the operator found F6. Null keeps the default.
     * Setting it after init also switches the live mode (the host's language
     * picker changed), which is why this is a setter and not a plain field.
     */
    @Input()
    set manglishDefault(value: ManglishMode | null | undefined) {
        if (!value || value === this.appliedDefault) {
            return;
        }
        this.appliedDefault = value;
        this.mode = value;
        this.resetPattern();
        // Only announce post-init changes; the initial value rides ngOnInit's
        // own emit, which happens after every input has been set.
        if (this.initialised && this.appManglish) {
            this.modeChange.emit(this.mode);
        }
    }

    /** Current in-progress ASCII pattern; null whenever the pattern resets. */
    readonly patternChange = output<string | null>();
    /** Fires with the active mode: once on init (when enabled) and on every F6. */
    readonly modeChange = output<ManglishMode>();

    private mode: ManglishMode = initialMode();
    private pattern: string | null = null;
    private patternStart = 0;
    private appliedDefault: ManglishMode | null = null;
    private initialised = false;

    constructor(private readonly elementRef: ElementRef<HTMLInputElement>) { }

    ngOnInit(): void {
        this.initialised = true;
        if (this.appManglish) {
            this.modeChange.emit(this.mode);
        }
    }

    onKeydown(event: KeyboardEvent): void {
        if (!this.appManglish) {
            return;
        }
        if (event.key === 'F6') {
            event.preventDefault();
            this.mode = this.mode === 'manglish' ? 'english' : 'manglish';
            this.resetPattern();
            this.modeChange.emit(this.mode);
            return;
        }
        if (this.mode !== 'manglish' || event.isComposing || event.ctrlKey || event.metaKey || event.altKey) {
            return;
        }
        if (event.key === 'Tab') {
            this.cycleAlternate(event);
            return;
        }
        if (event.key === 'Backspace' || event.key === 'Delete' || event.key === ' ') {
            // The browser performs the edit; only the pattern state resets.
            this.resetPattern();
            return;
        }
        if (event.key.length !== 1) {
            return;
        }
        event.preventDefault();
        this.applyPrintable(event.key);
    }

    onBlur(): void {
        if (this.appManglish) {
            this.resetPattern();
        }
    }

    /**
     * Replaces the currently committed glyph with the given alternate pattern
     * (e.g. 'ka3') — exactly what Tab-cycling to it would have produced — and
     * refocuses the input. Intended for host-rendered alternate strips. Only
     * alternates of the active pattern's base are accepted.
     */
    selectAlternate(patternKey: string): void {
        if (!this.appManglish || this.pattern === null || LAYOUT[this.pattern] === undefined) {
            return;
        }
        if (LAYOUT[patternKey] === undefined || splitPattern(patternKey)[0] !== splitPattern(this.pattern)[0]) {
            return;
        }
        this.replaceActiveGlyph(patternKey);
        this.elementRef.nativeElement.focus();
    }

    // ---- Internals ---------------------------------------------------------

    private applyPrintable(chr: string): void {
        const node = this.elementRef.nativeElement;
        const selStart = node.selectionStart ?? node.value.length;
        const selEnd = node.selectionEnd ?? selStart;

        const extended = this.pattern === null ? chr : this.pattern + chr;
        const continues = this.pattern !== null
            && extended.length <= MAX_PATTERN_LENGTH
            && LAYOUT[extended] !== undefined;

        if (continues) {
            this.pattern = extended;
        } else {
            this.pattern = chr;
            this.patternStart = selStart;
        }

        const glyph = LAYOUT[this.pattern];
        if (glyph !== undefined) {
            // Splice the glyph over the previous partial glyph (or the selection).
            this.spliceValue(this.patternStart, selEnd, glyph);
        } else {
            // No mapping: insert the key literally; pattern stays primed with it.
            this.spliceValue(selStart, selEnd, chr);
        }
        this.patternChange.emit(this.pattern);
    }

    private cycleAlternate(event: KeyboardEvent): void {
        if (this.pattern === null || LAYOUT[this.pattern] === undefined) {
            return; // no active glyph — leave Tab to the browser (focus move)
        }
        event.preventDefault();
        const [base, digits] = splitPattern(this.pattern);
        const current = digits ? parseInt(digits, 10) : 1;
        const candidate = base + (current + 1);
        this.replaceActiveGlyph(LAYOUT[candidate] !== undefined ? candidate : base);
    }

    /** Swaps the glyph committed for the active pattern with `pattern`'s glyph. */
    private replaceActiveGlyph(pattern: string): void {
        const currentGlyph = this.pattern === null ? undefined : LAYOUT[this.pattern];
        const glyph = LAYOUT[pattern];
        if (currentGlyph === undefined || glyph === undefined) {
            return;
        }
        this.spliceValue(this.patternStart, this.patternStart + currentGlyph.length, glyph);
        this.pattern = pattern;
        this.patternChange.emit(pattern);
    }

    private spliceValue(start: number, end: number, text: string): void {
        const node = this.elementRef.nativeElement;
        const scrollTop = node.scrollTop;
        node.value = node.value.slice(0, start) + text + node.value.slice(end);
        const caret = start + text.length;
        node.setSelectionRange(caret, caret);
        node.scrollTop = scrollTop;
        // The whole point of this directive vs the legacy one: downstream
        // (input) bindings / CVAs / reactive forms must observe the mutation.
        node.dispatchEvent(new Event('input', { bubbles: true }));
    }

    private resetPattern(): void {
        this.pattern = null;
        this.patternChange.emit(null);
    }
}
